// Locale-aware date/time formatting helpers.
// All "YYYY-MM-DD" strings are parsed as LOCAL dates (never UTC) so dates
// never shift by a day regardless of the user's timezone. Display formats
// use the browser's locale (e.g. 7/8/2026 in Australia, 8/7/2026 in the US,
// 12h vs 24h clocks) automatically.

/** Parse "YYYY-MM-DD" (local midnight), ISO datetimes, or Date objects into a Date. */
export const parseYMD = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== "string" || !value) return null;
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Format a Date as "YYYY-MM-DD" using LOCAL time (safe for API payloads / keys). */
export const toYMD = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** "7/8/2026" (AU) / "8/7/2026" (US) — numeric short date in the user's locale. */
export const fmtDateShort = (v) => {
  const d = parseYMD(v);
  return d ? d.toLocaleDateString() : "—";
};

/** "7 Aug 2026" (AU/GB) / "Aug 7, 2026" (US) — unambiguous medium date. */
export const fmtDateMed = (v) => {
  const d = parseYMD(v);
  return d ? d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";
};

/** "Fri, 7 Aug 2026" — medium date with weekday. */
export const fmtDateFull = (v) => {
  const d = parseYMD(v);
  return d
    ? d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" })
    : "—";
};

/** "Friday, 7 Aug 2026" — long weekday, used for day-section headers. */
export const fmtDateLong = (v) => {
  const d = parseYMD(v);
  return d
    ? d.toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "short", year: "numeric" })
    : "—";
};

/** "7 Aug" (AU/GB) / "Aug 7" (US) — day + month, no year. */
export const fmtDayMonth = (v) => {
  const d = parseYMD(v);
  return d ? d.toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "—";
};

/** "Fri" — short weekday name in the user's locale. */
export const fmtWeekday = (v) => {
  const d = parseYMD(v);
  return d ? d.toLocaleDateString(undefined, { weekday: "short" }) : "";
};

/** "Aug" — short month name in the user's locale. */
export const fmtMonthShort = (v) => {
  const d = parseYMD(v);
  return d ? d.toLocaleDateString(undefined, { month: "short" }) : "";
};

/** "7 Aug — 10 Aug 2026" — locale-aware date range. */
export const fmtRange = (start, end) => {
  if (!start && !end) return "—";
  if (!end || start === end) return fmtDateMed(start || end);
  return `${fmtDayMonth(start)} — ${fmtDateMed(end)}`;
};

/** "09:00" (HH:MM) → "9:00 am" (12h locales) or "09:00" (24h locales). */
export const fmtTime = (hhmm) => {
  if (typeof hhmm !== "string" || !hhmm.includes(":")) return hhmm || "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm;
  return new Date(2000, 0, 1, h, m).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

/** ISO datetime → "7 Aug 2026, 9:41 am" in the user's locale + timezone. */
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};
